import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { connectProviderPopup } from '@/lib/auth-provider-connect';
import { showProviderConnectionError } from '@/lib/auth-provider-error';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook that provides a provider connect handler using the popup OAuth flow.
 *
 * The popup resolves on either a `postMessage` from the OAuth callback
 * (gives us success/error data for the toast) or the parent window
 * regaining focus (gives us nothing, but signals the popup is gone).
 * Either way, the `finally` block refetches provider state so the row
 * updates from server state — covering Google's COOP case where
 * `postMessage` can't reach us.
 */
export function useProviderConnect() {
  const queryClient = useQueryClient();
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  const connect = useCallback(
    async (provider: string) => {
      setConnectingProvider(provider);
      try {
        const result = await connectProviderPopup(provider, 'link');
        if (result?.error) {
          showProviderConnectionError({
            provider,
            error: result.error,
            onRetry: () => void connect(provider),
          });
        } else if (result?.context?.linked === '1' || result?.token) {
          // `context.linked === '1'` is the link-mode signal (no
          // session swap); `token` is the legacy sign-in popup
          // success path retained for back-compat.
          toast.success(`${provider} connected`);
        }
      } finally {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.userProviders }),
          queryClient.invalidateQueries({ queryKey: queryKeys.userIdentities }),
          queryClient.invalidateQueries({ queryKey: queryKeys.onboardingState }),
        ]);
        setConnectingProvider(null);
      }
    },
    [queryClient],
  );

  return { connect, connectingProvider };
}
